// PSBT plumbing shared by every wallet provider.
//
// Before this module each provider carried its own copy of: parse a PSBT
// with a bitcoinjs fallback, re-serialize it, extract the raw transaction,
// strip BIP32 derivation paths, hand-finalize a taproot key-path input, and
// build a zero-value dummy PSBT for signature-based auth. Eight parsers,
// five finalizers, four dummy builders, all slightly different. This is the
// one copy. Providers own only what is specific to their extension's API.

import * as bitcoin from 'bitcoinjs-lib'
import * as btc from '@scure/btc-signer'

export const SIGHASH_SINGLE_ANYONECANPAY = btc.SignatureHash.SINGLE | btc.SignatureHash.ANYONECANPAY

/**
 * Parse a PSBT hex into a scure Transaction. Falls back through bitcoinjs,
 * which tolerates a few encodings scure rejects, and re-encodes. Legacy
 * inputs that carry only a witnessUtxo (older API builders) are accepted.
 */
export function parsePsbtTransaction(psbtHex: string): btc.Transaction {
	const bytes = Buffer.from(psbtHex, 'hex')
	try {
		return btc.Transaction.fromPSBT(bytes, { allowLegacyWitnessUtxo: true })
	} catch (scureError) {
		try {
			const viaBitcoinjs = bitcoin.Psbt.fromHex(psbtHex, { network: bitcoin.networks.bitcoin })
			return btc.Transaction.fromPSBT(Buffer.from(viaBitcoinjs.toHex(), 'hex'), {
				allowLegacyWitnessUtxo: true
			})
		} catch {
			throw scureError
		}
	}
}

export function parsePsbtBytes(psbt: Uint8Array): btc.Transaction {
	return parsePsbtTransaction(Buffer.from(psbt).toString('hex'))
}

export function toPsbtHex(tx: btc.Transaction): string {
	return Buffer.from(tx.toPSBT()).toString('hex')
}

export function toPsbtBase64(tx: btc.Transaction): string {
	return Buffer.from(tx.toPSBT()).toString('base64')
}

/** The raw transaction of a fully finalized PSBT, as hex. */
export function extractRawHex(tx: btc.Transaction): string {
	return Buffer.from(tx.extract()).toString('hex')
}

/** Round-trip a wallet's PSBT through scure so callers get one canonical encoding. */
export function normalizePsbtHex(psbtHex: string): string {
	return toPsbtHex(parsePsbtTransaction(psbtHex))
}

/** Extract the raw tx from a PSBT the wallet already finalized. */
export function extractFinalizedPsbtHex(psbtHex: string): string {
	return extractRawHex(parsePsbtTransaction(psbtHex))
}

export function allInputIndexes(tx: btc.Transaction): number[] {
	return Array.from({ length: tx.inputsLength }, (_, i) => i)
}

/** txid (display byte order) of the outpoint an input spends. */
export function inputTxidHex(tx: btc.Transaction, index: number): string {
	const txid = tx.getInput(index).txid
	if (!txid) throw new Error(`input ${index}: no previous txid`)
	return Buffer.from(txid).toString('hex')
}

/** Script of the prevout an input spends, from its witnessUtxo. */
export function inputScriptHex(tx: btc.Transaction, index: number): string | null {
	const script = tx.getInput(index).witnessUtxo?.script
	return script ? Buffer.from(script).toString('hex') : null
}

/**
 * Drop BIP32 derivation paths. Several extensions choke on paths that do
 * not belong to them, and none of them need ours.
 */
export function stripBip32Derivation(tx: btc.Transaction, options: { outputs?: boolean } = {}): void {
	for (let i = 0; i < tx.inputsLength; i++) {
		tx.updateInput(i, { tapBip32Derivation: [] })
	}
	if (options.outputs) {
		for (let i = 0; i < tx.outputsLength; i++) {
			tx.updateOutput(i, { tapBip32Derivation: [] })
		}
	}
}

/**
 * Finalize taproot key-path inputs by hand: the witness is the bare
 * Schnorr signature. Used where the extension returns a tapKeySig but does
 * not finalize (SINGLE|ANYONECANPAY escrows must keep the 65-byte
 * signature, which scure's generic finalize handles too, but this is what
 * every provider did and what the escrow verifier expects).
 */
export function finalizeTaprootKeyPath(tx: btc.Transaction, indexes: number[]): void {
	for (const i of indexes) {
		const input = tx.getInput(i)
		if (!input.tapKeySig || input.tapKeySig.length === 0) {
			throw new Error(`Wallet returned no signature for input ${i}`)
		}
		tx.updateInput(i, { finalScriptWitness: [input.tapKeySig] })
	}
}

/** Finalize the listed inputs (or all of them) with scure's generic finalizer. */
export function finalizeInputs(tx: btc.Transaction, indexes?: number[]): void {
	for (const i of indexes ?? allInputIndexes(tx)) {
		tx.finalizeIdx(i)
	}
}

/**
 * The zero-value, SINGLE|ANYONECANPAY dummy PSBT providers sign to prove
 * control of a script (profile edits, offer replies). Byte-for-byte what
 * the previous per-provider builders produced: version 0, locktime 0, one
 * null-outpoint input, one zero-value output back to the same script.
 */
export function dummySignaturePsbt(
	script: Uint8Array,
	extra: { redeemScript?: Uint8Array; tapInternalKey?: Uint8Array } = {}
): string {
	const tx = new btc.Transaction({ version: 0, lockTime: 0 })
	tx.addInput({
		txid: '0000000000000000000000000000000000000000000000000000000000000000',
		sequence: 4294967295,
		index: 0,
		witnessUtxo: { script, amount: 0n },
		sighashType: SIGHASH_SINGLE_ANYONECANPAY,
		...(extra.redeemScript ? { redeemScript: extra.redeemScript } : {}),
		...(extra.tapInternalKey ? { tapInternalKey: extra.tapInternalKey } : {})
	})
	tx.addOutput({ amount: 0n, script })
	return toPsbtHex(tx)
}

/** Dummy PSBT for a taproot key (33-byte compressed or 32-byte x-only). */
export function dummyTaprootPsbt(publicKeyHex: string): string {
	const pubkey = Buffer.from(publicKeyHex, 'hex')
	const xonly = pubkey.length === 33 ? pubkey.subarray(1) : pubkey
	const { script, tapInternalKey } = btc.p2tr(xonly)
	return dummySignaturePsbt(script, { tapInternalKey })
}

/** Dummy PSBT for a nested P2SH-P2WPKH payment key (Xverse). */
export function dummyNestedSegwitPsbt(publicKeyHex: string): string {
	const { script, redeemScript } = btc.p2sh(btc.p2wpkh(Buffer.from(publicKeyHex, 'hex')))
	return dummySignaturePsbt(script, { redeemScript })
}

/** Dummy PSBT for a native P2WPKH payment key (OYL, Leather). */
export function dummyNativeSegwitPsbt(publicKeyHex: string): string {
	const { script } = btc.p2wpkh(Buffer.from(publicKeyHex, 'hex'))
	return dummySignaturePsbt(script)
}
